import { useEffect, useState } from 'react';
import api from '../api';
import { useWindowWidth } from '../hooks';
import { TEAL, RED, BORD, MUTED, TEXT } from '../stockShared';

// What was just recorded, with Undo, for 10 seconds (the director, 26 Oct
// 2026: "Undo saved added/removed/counted item number for 10s after adding.
// But make sure you do not overlap the message with buttons for adding etc.
// On mobile, I would add the disappearing message on top of the screen").
//
// On a phone it covers the top bar, clear of every button below; on a
// desktop it sits low in the middle, clear of the list's ± buttons at the
// right. Undo writes the opposite of the lines (POST /stock/movements/undo):
// nothing is deleted, the history shows both.
//
// text: what was recorded   ids: the lines it wrote   onUndone(answer), onClose()

const SECONDS = 10;

export default function StockUndoBar({ text, ids, onUndone, onClose }) {
  const phone = useWindowWidth() < 700;
  const [state, setState] = useState('shown');   // shown | undoing | undone | failed
  const [error, setError] = useState(null);
  const [started] = useState(() => Date.now());
  const [, tick] = useState(0);

  useEffect(() => {
    if (state !== 'shown') return undefined;
    const t = setInterval(() => {
      tick(n => n + 1);
      if (Date.now() - started >= SECONDS * 1000) onClose();
    }, 250);
    return () => clearInterval(t);
  }, [state, started, onClose]);
  useEffect(() => {
    if (state !== 'undone') return undefined;
    const t = setTimeout(onClose, 2500);
    return () => clearTimeout(t);
  }, [state, onClose]);

  async function undo() {
    setState('undoing');
    try {
      const { data } = await api.post('/api/v1/stock/movements/undo', { ids });
      setState('undone');
      onUndone?.(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Not undone. Check the connection, or record a correction.');
      setState('failed');
    }
  }

  const left = Math.max(0, SECONDS * 1000 - (Date.now() - started));
  const place = phone
    ? { top: 0, left: 0, right: 0, borderRadius: '0 0 12px 12px', padding: 'calc(10px + env(safe-area-inset-top, 0px)) 14px 10px' }
    : { bottom: 24, left: '50%', transform: 'translateX(-50%)', width: 'min(560px, calc(100vw - 32px))', borderRadius: 12, padding: '11px 14px' };
  const tone = state === 'failed' ? RED : TEAL;

  return (
    <div role="status" aria-live="polite"
      style={{ position: 'fixed', zIndex: 1250, boxSizing: 'border-box', ...place, background: '#11161d', border: `1px solid ${tone}66`,
               boxShadow: '0 10px 30px rgba(0,0,0,0.55)', fontFamily: 'system-ui', color: TEXT, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <i className={`fa-solid ${state === 'failed' ? 'fa-circle-exclamation' : state === 'undone' ? 'fa-rotate-left' : 'fa-circle-check'}`} style={{ color: tone, fontSize: 15, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, lineHeight: 1.4 }}>
          {state === 'undone' ? 'Undone: back to how it was.' : state === 'failed' ? error : text}
        </div>
        {(state === 'shown' || state === 'undoing') && (
          <button type="button" onClick={undo} disabled={state === 'undoing'}
            style={{ flexShrink: 0, background: 'transparent', border: `1px solid ${TEAL}`, color: TEAL, borderRadius: 9, padding: '7px 14px', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'system-ui', minHeight: 38 }}>
            {state === 'undoing' ? 'Undoing…' : 'Undo'}
          </button>
        )}
        {state === 'failed' && (
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: MUTED, fontSize: 18, cursor: 'pointer' }}>✕</button>
        )}
      </div>
      {state === 'shown' && (
        <div aria-hidden="true" style={{ position: 'absolute', left: 0, bottom: 0, height: 3, background: BORD, width: '100%' }}>
          <div style={{ height: '100%', background: TEAL, width: `${(left / (SECONDS * 1000)) * 100}%`, transition: 'width 0.25s linear' }} />
        </div>
      )}
    </div>
  );
}
